import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ModalIsland, Button, Typography } from '@saviynt/design-system';
import { extendSession, ExtendResult, getSessionTiming } from 'ui-common/src/utils/sessionTiming';
import * as logoutUtil from '../../../ui-common/src/utils/logout';

import './SessionTimeoutModal.css';

/**
 * Computes timeout/modal display info from Redux session timing state.
 * @param {{ timeoutMins: number|null, promptMins: number|null, lastRefreshedAt: number|null }} info
 */
function getTimeoutInfo(info) {
  if (!info || !info.timeoutMins || !info.lastRefreshedAt) {
    return { featureEnabled: false };
  }

  // promptMins = 0 means "no prompt before timeout" — treat as disabled.
  if (!info.promptMins) {
    return { featureEnabled: false };
  }

  // Clamp promptMins so the modal always appears before logout.
  // At minimum, ensure 30 seconds of warning time.
  const promptMins = Math.min(info.promptMins, info.timeoutMins - 0.5);

  const ellapsedMillis = new Date().getTime() - info.lastRefreshedAt;
  const ellapsedSeconds = Math.floor(ellapsedMillis / 1000);

  const timeoutSeconds = info.timeoutMins * 60;
  const promptMinutes = info.timeoutMins - promptMins;
  const promptSeconds = promptMinutes * 60;

  return {
    featureEnabled: true,
    timeUntilModal: Math.max(promptSeconds - ellapsedSeconds, 0),
    shouldShowModal: ellapsedSeconds >= promptSeconds,
    timeUntilLogout: Math.max(timeoutSeconds - ellapsedSeconds, 0),
    shouldLogOut: ellapsedSeconds >= timeoutSeconds,
  };
}

const SessionTimeoutModal = () => {
  const sessionTiming = useSelector(getSessionTiming);

  const [timeUntilLogout, setTimeUntilLogout] = useState(0);
  const [shouldShowModal, setShouldShowModal] = useState(false);

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isExtendingRef = React.useRef(false);

  const sessionTimeout = () => {
    setIsLoggingOut(true);
    logoutUtil.logout(logoutUtil.LogoutTrigger.sessionTimeout);
  };

  const userLogout = () => {
    setIsLoggingOut(true);
    logoutUtil.logout(logoutUtil.LogoutTrigger.userInitiated);
  };

  const sessionExtendError = () => {
    setIsLoggingOut(true);
    logoutUtil.logout(logoutUtil.LogoutTrigger.sessionExtendError);
  };

  const handleExtendSession = async () => {
    // Prevents extending session on a logout action.
    if (isLoggingOut) return;

    isExtendingRef.current = true;
    setShouldShowModal(false);

    const { result } = await extendSession();

    isExtendingRef.current = false;

    if (result === ExtendResult.REDIRECT) {
      sessionExtendError();
    } else if (result === ExtendResult.FAILED) {
      // If something went wrong when trying to extend the session,
      // we should log out for security reasons.
      userLogout();
    }
    // On SUCCESS, Redux state is updated by extendSession() which triggers re-render.
  };

  useEffect(() => {
    let timeoutId = 0;
    let countdownId = 0;

    const updateInfo = () => {
      const timeoutInfo = getTimeoutInfo(sessionTiming);

      if (!timeoutInfo.featureEnabled) {
        setShouldShowModal(false);
      } else {
        if (!isExtendingRef.current) {
          setShouldShowModal(timeoutInfo.shouldShowModal);
        }
        setTimeUntilLogout(timeoutInfo.timeUntilLogout);
      }

      return timeoutInfo;
    };

    const refreshTimers = () => {
      clearTimeout(timeoutId);
      clearInterval(countdownId);

      const timeoutInfo = updateInfo();

      if (!timeoutInfo.featureEnabled) {
        return;
      }

      if (timeoutInfo.shouldLogOut) {
        sessionTimeout();
      } else if (timeoutInfo.shouldShowModal) {
        // Update time remaining before timeout
        countdownId = setInterval(() => {
          if (updateInfo().shouldLogOut) {
            refreshTimers();
          }
        }, 250); // Update 4x per second
      } else if (timeoutInfo.timeUntilModal >= 0) {
        // Timer to show modal
        timeoutId = setTimeout(() => {
          refreshTimers();
        }, timeoutInfo.timeUntilModal * 1000); // in milliseconds
      } else {
        // Error: log out
        sessionTimeout();
      }
    };

    refreshTimers();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(countdownId);
    };
  }, [sessionTiming]);

  const minutes = Math.floor(timeUntilLogout / 60);
  const seconds = timeUntilLogout % 60;
  const isOpen = shouldShowModal && !isLoggingOut;

  // Ensure the overlay for this security-critical modal sits above jQuery UI dialogs (z-index: 9999) from GSP.
  // The overlay is nested inside the header's stacking context, so CSS alone
  // can't reach it — we need to bump z-index on it directly.
  useEffect(() => {
    if (!isOpen) return undefined;

    const OVERLAY_Z = 10000;
    let prevZIndex = '';
    let overlayEl = null;

    // The overlay may not be in the DOM immediately when isOpen flips —
    // poll briefly until it appears.
    const maxAttempts = 20;
    let attempts = 0;

    const intervalId = setInterval(() => {
      overlayEl = document.querySelector('[data-testid="ds-global-overlay"]');
      attempts += 1;

      if (!overlayEl && attempts < maxAttempts) return;
      clearInterval(intervalId);

      if (!overlayEl) return;

      prevZIndex = overlayEl.style.zIndex;
      overlayEl.style.zIndex = OVERLAY_Z;
    }, 50); // Check every 50ms, up to 1 second

    return () => {
      clearInterval(intervalId);
      if (overlayEl) {
        overlayEl.style.zIndex = prevZIndex;
      }
    };
  }, [isOpen]);

  return (
    <ModalIsland
      kind="critical"
      isOpen={isOpen}
      onClose={handleExtendSession}
      hasPrivacyOverlay
      className="SessionTimeoutModal"
      HeaderComp={
        <>
          <Typography kind="h2" className="SessionTimeoutModal-title">
            <FormattedMessage
              id="SessionTimeoutModal.title"
              defaultMessage="You will be logged out soon due to inactivity"
            />
          </Typography>
          <Typography kind="display3" className="SessionTimeoutModal-timer">
            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
          </Typography>
        </>
      }
      primaryButton={
        <Button kind="outlined" size="medium" onClick={handleExtendSession}>
          <FormattedMessage
            id="SessionTimeoutModal.continueSession"
            defaultMessage="Continue Session"
          />
        </Button>
      }
      secondaryButton={
        <Button kind="ghost" size="medium" onClick={userLogout}>
          <FormattedMessage id="RequestHome.Logout" defaultMessage="Log Out" />
        </Button>
      }
    />
  );
};

export default SessionTimeoutModal;
